import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, fromEvent } from 'rxjs';
import { map, shareReplay, startWith, takeUntil } from 'rxjs/operators';

export enum ViewMode {
  PHONE = 'PHONE',
  PHABLET = 'PHABLET',
  TABLET = 'TABLET',
  DESKTOP = 'DESKTOP',
  LARGE_DESKTOP = 'LARGE_DESKTOP'
}

const MAX_WIDTH_PHONE = 575.9;
const MAX_WIDTH_PHABLET = 767.9;
const MAX_WIDTH_TABLET = 991.9;
const MAX_WIDTH_DESKTOP = 1279.9;

/**
 * Responsive Design unterstützender Service,
 * der abhängig von der aktuellen Viewport-Breite und den Pixel-Breakpoints der DPL
 * die Device-Kategorie entsprechend nach PDS
 * https://web.dev.ocp.webapp.idst.ibaintern.de/design/pds3/develop/#/201535451/270462891
 * ermittelt.
 *
 * PHONE < PHABLET < TABLET < DESKTOP < LARGE_DESKTOP
 *
 * Beschreibung aus Demo-APP:
 * @example
 * Der Screen-Size-Service kann eingebunden werden,
 * um die Displaygröße als Observable zu erhalten, um die Breakpoints nicht selbst setzen zu müssen.
 *
 * Verfügbare Streams:
 * onlyPhone$ - Viewport-Breite < 576px
 * onlyPhablet$ - Viewport-Breite 576px - 767px
 * onlyTablet$ - Viewport-Breite 768px - 991px
 * onlyDesktop$ - Viewport-Breite 992px - 1279px
 * onlyLargeDesktop$ - Viewport-Breite >= 1280px
 *
 * Alternativ können die Hilfsfunktionen inRange$(...), rangeFrom$(...) und rangeTo$(...)
 * genutzt werde, um Spannen zwischen, von bzw. bis einer bestimmten Kategorie
 * zu erfassen. Die Grenzen sind dabei immer inklusiv zu verstehen.
 */
@Injectable({
  providedIn: 'root'
})
export class ScreenSizeService implements OnDestroy {
  public static MAX_WIDTH_BY_MODE = {
    [ViewMode.PHONE]: MAX_WIDTH_PHONE,
    [ViewMode.PHABLET]: MAX_WIDTH_PHABLET,
    [ViewMode.TABLET]: MAX_WIDTH_TABLET,
    [ViewMode.DESKTOP]: MAX_WIDTH_DESKTOP,
    [ViewMode.LARGE_DESKTOP]: Number.MAX_VALUE
  };

  public static MIN_WIDTH_BY_MODE = {
    [ViewMode.PHONE]: 0,
    [ViewMode.PHABLET]: MAX_WIDTH_PHONE,
    [ViewMode.TABLET]: MAX_WIDTH_PHABLET,
    [ViewMode.DESKTOP]: MAX_WIDTH_TABLET,
    [ViewMode.LARGE_DESKTOP]: MAX_WIDTH_DESKTOP
  };

  private readonly ngUnsubscribe: Subject<void> = new Subject();

  /**
   * exponiert identifizierten ViewMode
   */
  public viewMode$: Observable<ViewMode> = fromEvent(window, 'resize').pipe(
    map((event) => (event.target as Window).innerWidth),
    startWith(window.innerWidth),
    map((width) => {
      const viewMode = Object.entries(ScreenSizeService.MAX_WIDTH_BY_MODE).find(
        ([_, modeWidth]) => {
          return width <= modeWidth;
        }
      )?.[0] as ViewMode;

      return viewMode ?? ViewMode.LARGE_DESKTOP;
    }),
    shareReplay(1),
    takeUntil(this.ngUnsubscribe)
  );

  /**
   * Diese Hilfsfunktion liefert ein Observable, das genau dann wahr enthält,
   * wenn die Breite des Viewports zwischen unterer und oberer Grenze fällt.
   * Die Grenzen sind dabei inklusive.
   *
   * Beispiel:
   * inRange$(ViewMode.TABLET, ViewMode.LARGE_DESKTOP) ist wahr, wenn
   * die Mindestbreite für Tablet erreicht ist und die Maximalbreite für
   * Large Desktop nicht überschritten wurde, welche Number.MAX_VALUE ist,
   * also nicht überschritten werden kann.
   *
   * @param lowerBound ViewMode, untere Grenze, inklusive
   * @param upperBound ViewMode, obere Grenze, inklusive
   */
  public inRange$(lowerBound: ViewMode, upperBound: ViewMode): Observable<boolean> {
    return fromEvent(window, 'resize').pipe(
      map((event) => (event.target as Window).innerWidth),
      startWith(window.innerWidth),
      map((width) => {
        return (
          ScreenSizeService.MIN_WIDTH_BY_MODE[lowerBound] < width &&
          width <= ScreenSizeService.MAX_WIDTH_BY_MODE[upperBound]
        );
      }),
      shareReplay(1),
      takeUntil(this.ngUnsubscribe)
    );
  }

  /**
   * Diese Hilfsfunktion liefert ein Observable, das genau dann wahr enthält,
   * wenn die Breite des Viewports über der unteren Grenze liegt.
   * Die Grenze ist dabei inklusive.
   *
   * Beispiel:
   * rangeFrom$(ViewMode.TABLET) ist wahr,
   * wenn die Mindestbreite für Tablet erreicht ist.
   * Entspricht dem CSS-Klassen-Infix "md" in Bootstrap-5.
   *
   * @param lowerBound ViewMode, untere Grenze, inklusive
   */
  public rangeFrom$(lowerBound: ViewMode): Observable<boolean> {
    return this.inRange$(lowerBound, ViewMode.LARGE_DESKTOP);
  }

  /**
   * Diese Hilfsfunktion liefert ein Observable, das genau dann wahr enthält,
   * wenn die Breite des Viewports unter der oberen Grenze liegt.
   * Die Grenze ist dabei inklusive.
   *
   * Beispiel:
   * rangeTo$(ViewMode.TABLET) ist wahr, wenn
   * die Maximalbreite für Tablet nicht überschritten wurde.
   *
   * @param upperBound ViewMode, obere Grenze, inklusive
   */
  public rangeTo$(upperBound: ViewMode): Observable<boolean> {
    return this.inRange$(ViewMode.PHONE, upperBound);
  }

  /**
   * true, gdw. ViewPort nicht mehr DPL-Breakpoint für Smartphones
   */
  public onlyPhone$: Observable<boolean> = this.viewMode$.pipe(
    map((mode) => mode === ViewMode.PHONE)
  );
  /**
   * true, gdw. ViewPort breiter als Smartphones und nicht mehr als DPL-Breakpoint für Phablets (Phone-Tablet-Hybride)
   */
  public onlyPhablet$: Observable<boolean> = this.viewMode$.pipe(
    map((mode) => mode === ViewMode.PHABLET)
  );
  /**
   * true, gdw. ViewPort breiter als Phablet und nicht mehr als DPL-Breakpoint für Tablet
   */
  public onlyTablet$: Observable<boolean> = this.viewMode$.pipe(
    map((mode) => mode === ViewMode.TABLET)
  );
  /**
   * true, gdw. ViewPort breiter als Tablet-DPL-Breakpoint
   */
  public onlyDesktop$: Observable<boolean> = this.viewMode$.pipe(
    map((mode) => mode === ViewMode.DESKTOP)
  );
  /**
   * true, gdw. ViewPort breiter als Desktop-DPL-Breakpoint
   */
  public onlyLargeDesktop$: Observable<boolean> = this.viewMode$.pipe(
    map((mode) => mode === ViewMode.LARGE_DESKTOP)
  );

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
